/**
 * Utilitaires pour la gestion des dates en UTC
 *
 * Ces fonctions normalisent les dates sélectionnées via des datepickers
 * pour garantir que les jours restent inclusifs quel que soit le fuseau horaire.
 *
 * Problème résolu : react-day-picker retourne un Date local (minuit dans le fuseau de l'utilisateur).
 * Quand on fait .toISOString(), la conversion en UTC peut décaler d'un jour selon le fuseau.
 *
 * Solution : créer explicitement des dates UTC pour le début et la fin de journée.
 */

/**
 * Convertit une date locale en début de journée UTC (00:00:00.000)
 * @param date - Date locale retournée par le datepicker
 * @returns Date UTC représentant le début de la journée sélectionnée
 */
export const toUtcStartOfDay = (date: Date): Date => {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0));
};

/**
 * Convertit une date locale en fin de journée UTC (23:59:59.999)
 * @param date - Date locale retournée par le datepicker
 * @returns Date UTC représentant la fin de la journée sélectionnée
 */
export const toUtcEndOfDay = (date: Date): Date => {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999));
};

/** Retourne la date du jour à minuit dans le fuseau local. */
export const getStartOfToday = (): Date => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

/** Parse une Date ou une chaîne yyyy-MM-dd sans décalage de fuseau horaire. */
export const fromDateInputValue = (value?: Date | string | null): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value;

  return new Date(`${value.slice(0, 10)}T00:00:00`);
};

/** Formate une date locale en chaîne yyyy-MM-dd pour les champs formulaire. */
export const toDateInputString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

/**
 * Résout la date d'expiration des points renvoyée par le back.
 *
 * Le champ `pointExpirationDate` du logic workspace contient soit :
 * - une date ISO (`2026-12-31T23:59:59+01:00`),
 * - soit l'expression relative `last day of December this year[ + N years], 23:59:59`,
 *   résolue ici au 31 décembre de l'année courante (+ N).
 *
 * @returns la Date d'expiration, ou `null` si la valeur est vide/non reconnue.
 */
export const resolvePointExpirationDate = (raw?: string | null): Date | null => {
  if (!raw) {
    return null;
  }

  const isoDate = new Date(raw);
  if (!Number.isNaN(isoDate.getTime())) {
    return isoDate;
  }

  const relativeMatch = raw.trim().match(/^last day of December this year(?: \+ (\d+) years?)?/);
  if (relativeMatch) {
    const year = new Date().getFullYear() + Number(relativeMatch[1] ?? 0);
    return new Date(year, 11, 31);
  }

  return null;
};
